import { IPriceRule } from '../../types/price.types';
import { INestedPricingCategory, IPricingCategory } from '../../api/getPricingCategories/types';

/**
 * Finds the inherited rule for a pricing category by walking up the category hierarchy.
 *
 * Inheritance logic:
 * 1. If an override rule exists, return undefined (no inheritance)
 * 2. Walk up parent categories looking for a matching rule
 * 3. Fall back to the base rule (rule with no category assigned)
 *
 * @param category - The category to find the inherited rule for
 * @param parentId - The price band or extension instance ID
 * @param parentType - Whether this is a price_band or extension_instance
 * @param priceRules - All available price rules
 * @param categoryMap - Map of categories for efficient parent lookups
 * @param overrideRule - The override rule for this category, if it exists
 * @returns The inherited rule, or undefined if override exists or no rule is inherited
 */
export const findInheritedRule = (
  category: INestedPricingCategory,
  parentId: string,
  parentType: 'price_band' | 'extension_instance',
  priceRules: IPriceRule[],
  categoryMap: Map<string, IPricingCategory>,
  overrideRule?: IPriceRule,
): IPriceRule | undefined => {
  // No inheritance if there's an override
  if (overrideRule) return undefined;

  const findRuleInHierarchy = (categoryId: string | null): IPriceRule | undefined => {
    // Find a pricing rule matching this category (or if null, the price band/channel base rule)
    const rule = priceRules.find((r) => {
      // Skip placeholder rules
      if (r.price_rule_id?.endsWith('_rule')) return false;

      // Rule must belong to the same price band or channel
      const ruleParentId = parentType === 'price_band' ? r.price_band?.id : r.extension_instance?.id;
      if (ruleParentId !== parentId) return false;

      // Match this category, or if null, the price band/channel rule
      const ruleCategoryId = r.pricing_category?.id ?? r.pricing_category_id;
      return categoryId === null ? ruleCategoryId == null : ruleCategoryId === categoryId;
    });

    if (rule) return rule;

    // No more parent categories to search
    if (!categoryId) return undefined;

    const parentCategory = categoryMap.get(categoryId);
    return findRuleInHierarchy(parentCategory?.parent_category_id ?? null);
  };

  return findRuleInHierarchy(category.parent_category_id ?? null);
};
